/**
 * Copied from NftItemTileMedia.tsx
 * Should be placed in own package before merge
 */

import { FunctionComponent, useEffect, useRef, useCallback, useState } from 'react';
import { ItemMediaSpinner } from 'dibs-elements/exports/ItemMediaSpinner';
import { useLazyLoadMedia, READY_STATES } from 'dibs-elements/exports/useLazyLoadMedia';
import {
    VisibilityTracker,
    VisibilityChangeCallback,
} from 'dibs-visibility-tracker/exports/VisibilityTracker';
import styles from './VideoTile.scss';
import { Property } from 'csstype';

export type VideoTileProps = {
    sourceUrl: string;
    animate?: boolean;
    posterOverride?: string;
    resetOnPause?: boolean;
    observerOptions?: IntersectionObserverInit;
    objectFit?: Property.ObjectFit;
    objectPosition?: Property.ObjectPosition;
};

export const VideoTile: FunctionComponent<VideoTileProps> = ({
    sourceUrl,
    animate = true,
    resetOnPause = false,
    posterOverride,
    observerOptions = {
        rootMargin: `15% 0px 15% 0px`, // start loading just outside the viewport
    },
    objectFit = 'cover',
    objectPosition = 'initial',
}) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const sourceRef = useRef<HTMLSourceElement | null>(null);
    const [isPosterReplaced, setIsPosterReplaced] = useState(false);
    const lazyLoad = useLazyLoadMedia('video', {
        ref: videoRef,
        excludedHandlers: 'onSuspend onWaiting',
        shimmer: false,
    });

    useEffect(() => {
        if (videoRef.current && lazyLoad.readyState >= READY_STATES.HAVE_CURRENT_DATA) {
            // setting playback time on the video replaces the poster with the frame at the
            // given time
            if (!posterOverride) {
                videoRef.current.currentTime = 0;
            }
            setIsPosterReplaced(true);
        }
    }, [lazyLoad.readyState, posterOverride]);

    useEffect(() => {
        // only autoplay if enough data has been loaded to replace the poster
        if (!isPosterReplaced) return;

        const ref = videoRef.current;
        let playPromise: Promise<void> | undefined;

        if (animate) {
            playPromise = ref?.play();
        } else if (resetOnPause) {
            ref?.load();
        }

        // eslint-disable-next-line consistent-return
        return () => {
            playPromise?.then(() => ref?.pause());
        };
    }, [resetOnPause, animate, isPosterReplaced]);

    const handleVisible: (args: VisibilityChangeCallback) => void = useCallback(
        args => {
            if (!(sourceRef.current && videoRef.current)) return;
            if (args.isVisible) {
                // start loading data if video is in viewport
                sourceRef.current.src = sourceUrl;
                videoRef.current.load();
            }
        },
        [sourceUrl]
    );

    return (
        <>
            <VisibilityTracker
                elementRef={videoRef}
                onVisibilityChange={handleVisible}
                watchAfterFirstVisible={false}
                observerOptions={observerOptions}
            />
            <ItemMediaSpinner isOpen={!isPosterReplaced} />
            <video
                {...lazyLoad.handlerProps}
                style={{
                    objectFit,
                    objectPosition,
                }}
                poster={posterOverride}
                ref={videoRef}
                className={styles.media}
                muted
                loop
                playsInline
                preload="auto"
            >
                <source ref={sourceRef} type="video/mp4" />
            </video>
        </>
    );
};
